import { Head } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { AnalyticsErrorState } from "@/components/brand/analytics/analytics-error-state"
import { BrandAnalyticsTabs } from "@/components/brand/analytics/brand-analytics-tabs"
import { CrewAnalyticsEmpty } from "@/components/brand/analytics/crew/crew-analytics-empty"
import { CrewAnalyticsKpiStrip } from "@/components/brand/analytics/crew/crew-analytics-kpi-strip"
import { CrewAnalyticsWindowToggle } from "@/components/brand/analytics/crew/crew-analytics-window-toggle"
import { CrewAtRiskTable } from "@/components/brand/analytics/crew/crew-at-risk-table"
import { CrewEngagementHistogram } from "@/components/brand/analytics/crew/crew-engagement-histogram"
import { CrewTopPerformersTable } from "@/components/brand/analytics/crew/crew-top-performers-table"
import AppLayout from "@/layouts/app-layout"
import type { CrewAnalyticsProps } from "@/types/crew-analytics"

const RELOAD_KEYS_ON_ERROR = [
  "state",
  "window",
  "kpis_snapshot",
  "kpis_window",
  "at_risk",
  "top_performers",
  "engagement",
]

/**
 * OKS-65 — Crew-wide analytics overview (`/brand/analytics/crew`).
 *
 * # State branch (TRD K11)
 *
 *   1. `state === "error"` → `<AnalyticsErrorState>` with Retry.
 *   2. `state === "cold_start"` → `<CrewAnalyticsEmpty>` full-page card
 *      with CTA to `candidatesPath()`.
 *   3. `state === "success"` → tab strip + header + window toggle + KPI
 *      strip + at-risk table + top-performers table + engagement
 *      histogram. Sub-empties are component-internal.
 *
 * # Partial reload (TRD K12 — PRD §5 split-KPI lock)
 *
 * Window toggle change → `router.get` with `only: ['kpis_window',
 * 'top_performers', 'engagement']`. `kpis_snapshot` + `at_risk` +
 * `state` + `window` are window-invariant and NEVER reload on toggle.
 */
export default function BrandAnalyticsCrewIndex({
  state,
  window,
  kpis_snapshot: kpisSnapshot,
  kpis_window: kpisWindow,
  at_risk: atRisk,
  top_performers: topPerformers,
  engagement,
}: CrewAnalyticsProps) {
  const { t } = useTranslation()

  return (
    <AppLayout>
      <Head title={t("analytics.crew.head_title")} />
      <div
        className="flex flex-col gap-6 p-4 md:p-6"
        data-testid="crew-analytics-page"
      >
        <BrandAnalyticsTabs active="crew" />

        <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold tracking-tight">
              {t("analytics.crew.heading")}
            </h1>
            <p className="text-muted-foreground text-sm">
              {t("analytics.crew.subheading")}
            </p>
          </div>
          {state === "success" && <CrewAnalyticsWindowToggle value={window} />}
        </header>

        {state === "error" ? (
          <AnalyticsErrorState reloadOnly={RELOAD_KEYS_ON_ERROR} />
        ) : state === "cold_start" ? (
          <CrewAnalyticsEmpty />
        ) : (
          <>
            {kpisSnapshot && kpisWindow && (
              <CrewAnalyticsKpiStrip
                snapshot={kpisSnapshot}
                window={kpisWindow}
                windowLabel={window}
              />
            )}
            {atRisk && <CrewAtRiskTable rows={atRisk} />}
            {topPerformers && (
              <CrewTopPerformersTable
                rows={topPerformers}
                windowLabel={window}
              />
            )}
            {engagement && (
              <CrewEngagementHistogram
                engagement={engagement}
                windowLabel={window}
              />
            )}
          </>
        )}
      </div>
    </AppLayout>
  )
}
